import {
  BULK_INVITE_REQUIRED_FIELDS,
  type BulkInviteCanonicalField,
  type BulkInviteInitiator,
  type BulkInviteResolvedColumn,
  type BulkInviteStatus,
} from '@qse/api'

/** Client-side courtesy guard; the backend is the source of truth. */
export const BULK_INVITE_MAX_FILE_BYTES = 1024 * 1024 // 1 MB

/**
 * Accepted upload types. Note `application/vnd.ms-excel` is genuinely ambiguous —
 * browsers report it for both `.xls` and (on some systems) `.csv`; the backend
 * dispatches on the real content, so listing both extensions here is correct.
 */
export const BULK_INVITE_ACCEPT: Record<string, string[]> = {
  'text/csv': ['.csv'],
  'application/vnd.ms-excel': ['.csv', '.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
}

export const BULK_INVITE_ACCEPT_EXTENSIONS = ['.csv', '.xlsx', '.xls']

/** Human labels for the mapping screen's dropdown. */
export const CANONICAL_FIELD_LABELS: Record<BulkInviteCanonicalField, string> = {
  first_name: 'First name',
  last_name: 'Last name',
  email: 'Email',
  phone_number: 'Phone number',
  role: 'Role',
  storage_quota: 'Storage quota',
  api_quota: 'API quota',
}

export const isRequiredField = (field: BulkInviteCanonicalField): boolean =>
  BULK_INVITE_REQUIRED_FIELDS.includes(field)

/**
 * Which required fields no column currently supplies. Drives both the screen's
 * top-of-page flags and the Confirm gate, so the admin can't advance into a send
 * the server would only reject.
 */
export const getUnresolvedRequired = (
  mapping: BulkInviteResolvedColumn[],
): BulkInviteCanonicalField[] => {
  const mapped = new Set(mapping.map(column => column.field).filter(Boolean))

  return BULK_INVITE_REQUIRED_FIELDS.filter(field => !mapped.has(field))
}

/** Fields claimed by two or more columns — the admin must drop one. */
export const getDuplicateFields = (
  mapping: BulkInviteResolvedColumn[],
): BulkInviteCanonicalField[] => {
  const seen = new Set<BulkInviteCanonicalField>()
  const duplicates = new Set<BulkInviteCanonicalField>()

  for (const column of mapping) {
    if (!column.field) continue
    if (seen.has(column.field)) duplicates.add(column.field)
    seen.add(column.field)
  }

  return [...duplicates]
}

/** A mapping is sendable when every required field is mapped exactly once. */
export const isMappingValid = (mapping: BulkInviteResolvedColumn[]): boolean =>
  getUnresolvedRequired(mapping).length === 0 && getDuplicateFields(mapping).length === 0

/** Display name for an upload's initiator; super-admin uploads always show as "Super Admin". */
export const formatInitiator = (initiator: BulkInviteInitiator | null): string => {
  if (!initiator) return '—'
  if (initiator.isSuperAdmin) return 'Super Admin'

  const name = `${initiator.firstName ?? ''} ${initiator.lastName ?? ''}`.trim()

  return name || initiator.email || '—'
}

export interface BulkInviteStatusMeta {
  label: string
  /** Tailwind classes for a status chip (bg + text). */
  className: string
}

export const getBulkInviteStatusMeta = (status: BulkInviteStatus): BulkInviteStatusMeta => {
  switch (status) {
    case 'QUEUED':
      return { label: 'Queued', className: 'bg-gray-400/15 text-gray-500 dark:text-gray-300' }
    case 'PROCESSING':
      return { label: 'Processing', className: 'bg-blue-500/15 text-blue-500' }
    case 'SUCCEEDED':
      return { label: 'Succeeded', className: 'bg-green-500/15 text-green-600 dark:text-green-400' }
    case 'PARTIAL':
      return { label: 'Partial', className: 'bg-amber-500/15 text-amber-500' }
    case 'FAILED':
      return { label: 'Failed', className: 'bg-red-500/15 text-red-500' }
    default:
      return { label: status, className: 'bg-gray-400/15 text-gray-500' }
  }
}

/**
 * Rows with a settled outcome. Skipped rows count: they were never attempted, but
 * they are final, and the invariant `success + failure + skipped = totalRows` means
 * excluding them would strand the bar short of 100% on any file with a bad row.
 */
export const getBulkInviteResolvedRows = (request: {
  successCount: number
  failureCount: number
  skippedCount: number
}): number => request.successCount + request.failureCount + request.skippedCount

/** Resolved rows as a percentage of total, clamped to 0–100. */
export const getBulkInviteProgress = (request: {
  totalRows: number
  successCount: number
  failureCount: number
  skippedCount: number
}): number => {
  if (!request.totalRows) return 0

  // Floor so the bar never reads 100% until every row is actually resolved.
  return Math.min(100, Math.floor((getBulkInviteResolvedRows(request) / request.totalRows) * 100))
}
